import 'add-to-calendar-button';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
// next
import { useRouter } from 'next/router';
// @mui
import {
  Card,
  CardHeader,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogContentText,
  DialogActions,
  Typography,
  Stack,
  Link,
  Button,
  Box,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { alpha } from '@mui/material/styles';
// hooks
import useResponsive from '../../hooks/useResponsive';
// components
import DIOText from '../../components/DIOText';
import Iconify from '../../components/Iconify';
import SvgIconStyle from '../../components/SvgIconStyle';
import {
  Before3WayCheckPrepDialog,
  MessageDialog,
  MessagePopover,
} from './';
// utils
import axios from '../../utils/axios';
import { fKrDateDayKST, fKrTimeKST, fYYYY_MM_DDInKST, fHH_MI_InKST } from '../../utils/formatTime';
// config
import { EVENT_ID } from '../../config';

// ---------

export default function Waiting3WayMeetingForm({ companyId, companyName, crewNickname, status, meetingTime, meetingLink, reloadOffer, checkList=null, prepList=null, fromRemind=false, weekend=false, holiday=false }) {
  const { enqueueSnackbar } = useSnackbar();
  const router = useRouter();
  const { oid } = router.query;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notAvailableSchedule, setNotAvailableSchedule] = useState(false);
  const [openBefore3WayCheckPrepDialog, setOpenBefore3WayCheckPrepDialog] = useState(false);
  const smUp = useResponsive('up', 'sm');
  const showConfirmButton = status === "waiting-3way-meeting";
  // calendar event
  const meetingStartTime = new Date(meetingTime);
  const meetingEndTime = new Date(meetingTime);
  meetingEndTime.setMinutes(meetingEndTime.getMinutes() + 40);
  const isHolidayOrWeekend = weekend || holiday;

  const copyLink = async () => {
    try {
      // 임시 textarea 요소 생성
      const textarea = document.createElement('textarea');
      textarea.value = meetingLink;
      textarea.style.position = 'fixed';
      textarea.style.top = '-1000px';
      document.body.appendChild(textarea);

      // 텍스트 선택 및 복사
      textarea.focus();
      textarea.select();
      textarea.setSelectionRange(0, 99999);
      const success = document.execCommand('copy');
      if (!success) {
        await navigator.clipboard.writeText(meetingLink);
      }
      document.body.removeChild(textarea);
      enqueueSnackbar('Zoom 링크를 복사하였습니다', { variant: 'info' });
    } catch (err) {
      enqueueSnackbar('링크를 복사하는데 실패하였습니다. 링크를 직접 복사해주세요.', { variant: 'error' });
      console.error('failed to copy: ', err);
    }
  }

  const openRequestChatDialog = () => {
    setNotAvailableSchedule(true);
  };

  const closeRequestChatDialog = () => {
    setNotAvailableSchedule(false);
  };

  const postConfirm = async () => {
    try {
      setIsSubmitting(true);
      await axios.post(`api/crews/offers/${oid}/confirm-3way-meeting/`);
      enqueueSnackbar('미팅 시간을 확인하셨습니다');
      setOpenBefore3WayCheckPrepDialog(false);
      reloadOffer();
      setIsSubmitting(false);
    } catch (error) {
      setIsSubmitting(false);
      console.error(error);
    }
  }

  const confirmMeeting = async () => {
    if (checkList && prepList) {
      setOpenBefore3WayCheckPrepDialog(true);
    } else {
      await postConfirm();
    }
  }

  return (
    <>
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
      <Stack>
        <Typography variant="h4" gutterBottom>
          {fromRemind
            ? "곧 사전 미팅이 시작됩니다. 제시간에 입장해주세요!"
            : "사전 미팅 일정이 확정되었습니다."
          }
        </Typography>
        {!fromRemind && (
        <Typography variant={smUp ? "body1" : "body2"}>
          미팅 시간을 확인하고, 미팅을 준비해주세요!
        </Typography>
        )}
        {!smUp && (
          <Box sx={{ mt: 2 }}>
            <MessageDialog companyId={companyId} companyName={companyName} />
          </Box>
        )}
      </Stack>
      {smUp && (
        <Box>
          <MessagePopover companyId={companyId} companyName={companyName} />
        </Box>
      )}
    </Stack>
    <Card
      sx={{
        mb: 3,
     }}
    >
      <CardHeader
        title="사전 미팅 안내"
        titleTypographyProps={{ variant: "h5", gutterBottom: true }}
        subheader={
          showConfirmButton
          ? "아래 일정을 확인하시면, Zoom 미팅 링크와 미팅 안내 사항을 알려드립니다."
          : "안내해드린 시간에 아래 Zoom 링크로 미팅에 참여해주세요."
        }
        subheaderTypographyProps={{ variant: smUp ? "body1" : "body2", color: "primary" }}
      />
      <CardContent sx={{ pt: isHolidayOrWeekend ? 1 : 2 }}>
      <Stack spacing={2} sx={{ mb: { xs: showConfirmButton ? 5 : 2, sm: 1.5 } }}>
        {/* 주말/공휴일 경고 */}
        {isHolidayOrWeekend && (
          <Box sx={{
            p: 2,
            bgcolor: (theme) => alpha(theme.palette.warning.main, 0.08),
            borderRadius: 1,
            border: (theme) => `1px solid ${alpha(theme.palette.warning.main, 0.2)}`
          }}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Iconify icon="eva:alert-triangle-outline" sx={{ color: 'warning.main', width: 20, height: 20, flexShrink: 0, mt: 0.25 }} />
              <Typography variant="body2" color="warning.darker">
                한국 기준 주말 및 공휴일 미팅은 운영팀 미운영 시간으로, 상대 측 노쇼나 미팅 링크오류 발생 시 즉각적인 대응이 어려울 수 있습니다.<br/>이 경우 DIO 카카오톡 채널로 문의를 남겨주시면, 운영 시간에 확인 후 미팅 일정 재조율을 도와드리겠습니다.
              </Typography>
            </Stack>
          </Box>
        )}
        {/* date */}
        <Stack direction="row" spacing={1} alignItems="center">
          <SvgIconStyle src={`/icons/ic_calendar.svg`} sx={{ color: "secondary.main" }} />
          <Typography variant="subtitle1">
            {fKrDateDayKST(meetingTime)}
          </Typography>
        </Stack>
        {/* time */}
        <Stack direction="row" spacing={1} alignItems="center">
          <SvgIconStyle src={`/icons/icon-clock-mono.svg`} sx={{ color: "secondary.main" }} />
          <Typography variant="subtitle1">
            {fKrTimeKST(meetingTime)}
          </Typography>
        </Stack>

        {/* 참석자 안내 */}
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <SvgIconStyle src={`/icons/icon-user-two-mono.svg`} sx={{ color: "secondary.main" }} />
          <Stack>
            <Typography variant="h6">
              참석자 2명 + <DIOText /> 레코딩 봇
            </Typography>
            <Typography variant="subtitle1" sx={{ color: "text.secondary" }}>
              {companyName} 담당자, {crewNickname} 크루
            </Typography>
          </Stack>
        </Stack>

        {/* zoom link */}
        {!showConfirmButton && (
        <Stack spacing={0.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <SvgIconStyle src={`/icons/icon-link-mono.svg`} sx={{ color: "secondary.main" }} />
            <Typography variant="subtitle1">
              Zoom 링크
            </Typography>
            {meetingLink && <Button
              size="small"
              variant="outlined"
              sx={{ borderColor: (theme) => theme.palette.grey[500_32] }}
              onClick={copyLink}
              startIcon={<SvgIconStyle src={`/icons/icon-share-mono.svg`} sx={{ width: 18, height: 18, color: "#8B95A1"}} />}
            >
              복사하기
            </Button>}
          </Stack>
          <Stack sx={{ pl: 4, alignItems: "flex-start" }} spacing={0.5}>
            {meetingLink ? (
              <Link variant="subtitle1" underline="always" sx={{ wordBreak: "break-all", textDecorationColor: (theme) => theme.palette.primary.main }} color="text.primary" href={meetingLink} target="_blank" rel="noopener">
                {meetingLink}
              </Link>
            ) : (
              <Box sx={{ 
                p: 2, 
                bgcolor: (theme) => alpha(theme.palette.warning.main, 0.1),
                borderRadius: 1,
                border: (theme) => `1px solid ${alpha(theme.palette.warning.main, 0.3)}`
              }}>
                <Typography variant="body2" color="warning.dark" sx={{ fontWeight: 500 }}>
                  ⚠️ Zoom 링크가 아직 생성되지 않았습니다.
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  관리자에게 문의해주세요. (support@dio.so)
                </Typography>
              </Box>
            )}
          </Stack>
          <Stack sx={{ pl: 4, alignItems: "flex-start" }} spacing={0.5}>
            <Typography variant="body2" sx={{ wordBreak: "break-all" }} color="text.secondary">
              Zoom 사전 미팅은 미팅 위반 행위(크루의 개인정보를 묻는 행위, 욕설 등) 체크를 위해 DIO에서 레코딩하며, 7일간 저장 후 파기됩니다.
            </Typography>
          </Stack>
        </Stack>
        )}

        {!showConfirmButton && fYYYY_MM_DDInKST(meetingStartTime) && fYYYY_MM_DDInKST(meetingEndTime) && (
        <Stack spacing={0.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <SvgIconStyle src={`/icons/icon-bookmark-mono.svg`} sx={{ color: "secondary.main" }} />
            <Typography variant="subtitle1">
              달력에 일정 추가하기
            </Typography>
          </Stack>
          <Box sx={{ pl: 4 }}>
            <add-to-calendar-button
              name={`DIO 사전미팅 - ${companyName}`}
              description="Zoom 링크로 미팅에 참여해주세요."
              location={meetingLink}
              startDate={fYYYY_MM_DDInKST(meetingStartTime)}
              endDate={fYYYY_MM_DDInKST(meetingEndTime)}
              startTime={fHH_MI_InKST(meetingStartTime)}
              endTime={fHH_MI_InKST(meetingEndTime)}
              timeZone="Asia/Seoul"  // 달력 이벤트 생성시 한국 시간으로 고정
              options="'Google', 'Apple', 'Outlook.com'"
              buttonsList
              hideTextLabelButton
              buttonStyle="round"
              lightMode="light"
              language="ko"
              styleLight="--btn-background: #ffffff; --btn-hover-background: #F4F6F8; --btn-border: #DFE3E8; --btn-shadow: none; --btn-active-shadow: none; --btn-hover-shadow: none;"
            />
          </Box>
        </Stack>
        )}

        {fromRemind && (
          <Box
            sx={{
              py: 3,
              px: { xs: 3, sm: 4 },
              background: (theme) => alpha(theme.palette.secondary.main, 0.08),
              borderRadius: 2,
            }}
          >
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1 }}>
              <Iconify icon={'eva:alert-circle-fill'} sx={{ color: "secondary.main" }} width={20} height={20} />
              <Typography variant="subtitle1" sx={{ color: "secondary.main" }}>
                미팅이 끝나면,
              </Typography>
            </Stack>
            <Typography variant="body1">
              <b>크루가 먼저 계약 조건(금액, 업무 시간 등)을 제안</b>하게 됩니다.
            </Typography>
          </Box>
        )}

      </Stack>
      {showConfirmButton && (
      <Stack spacing={2} direction={{ xs: "column-reverse", sm: "row" }} justifyContent={"flex-end"}>
        <LoadingButton
          id={EVENT_ID.crewOffer3WayRequestChat}
          type="button"
          loading={isSubmitting}
          onClick={openRequestChatDialog}
          sx={{ px: 3 }}
        >
          미팅 시간 변경 요청하기
        </LoadingButton>
        <LoadingButton
          id={EVENT_ID.crewOffer3WayConfirm}
          type="button"
          variant="contained"
          loading={isSubmitting}
          onClick={confirmMeeting}
          sx={{ px: 3 }}
        >
          미팅 일정을 확인했어요
        </LoadingButton>
      </Stack>
      )}
      </CardContent>
    </Card>
    <Before3WayCheckPrepDialog
      isOpen={openBefore3WayCheckPrepDialog}
      closeDialog={() => setOpenBefore3WayCheckPrepDialog(false)}
      checkList={checkList}
      prepList={prepList}
      confirm={postConfirm}
    />
    <Dialog open={notAvailableSchedule} onClose={closeRequestChatDialog} maxWidth="xs">
      <DialogTitle>앗! 미팅이 어려운 시간인가요?</DialogTitle>
      <DialogContent>
        <DialogContentText id="not-available-schedule">
          카카오톡 채팅으로 가능한 시간을 전달해 주시면, 회사와 미팅 시간을 조율해 드릴게요.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <LoadingButton
          variant="contained"
          endIcon={<Iconify icon="eva:external-link-outline" />}
        >
          <Link
            color="inherit"
            href="http://pf.kakao.com/_xbEWUs/chat"
            target="_blank"
            rel="noopener"
            underline="none"
          >
            카카오톡 채팅 열기
          </Link>
        </LoadingButton>
      </DialogActions>
    </Dialog>
    </>
  )
}

